import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { AdBanner, useShowAds } from '../components/AdBanner';
import { AalimLogoCompact } from '../components/AalimLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
    Send, 
    Plus, 
    MessageSquare, 
    Trash2, 
    Loader2,
    BookOpen,
    Crown,
    ChevronLeft,
    ChevronRight,
    AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatPage() {
    const navigate = useNavigate();
    const { user, token, isPremium } = useAuth();
    const showAds = useShowAds(isPremium);
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
    const [showBottomAd, setShowBottomAd] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchChatHistory = async () => {
        try {
            const response = await axios.get(`${API}/chat/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSessions(response.data.filter(s => s.mode === 'chat'));
        } catch (error) {
            console.error('Failed to fetch chat history:', error);
        } finally {
            setLoadingSessions(false);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        fetchChatHistory();
    }, [token]);

    const loadSession = async (sessionId) => {
        try {
            const response = await axios.get(`${API}/chat/session/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentSession(response.data);
            setMessages(response.data.messages || []);
        } catch (error) {
            toast.error('Failed to load session');
        }
    };

    const startNewChat = () => {
        // Generate a completely new session ID to reset LLM state
        // This mimics what happens on login - fresh session, fresh budget tracking
        setCurrentSession(null);
        setMessages([]);
        // Force a refresh of the session list
        fetchChatHistory();
        // Auto-close sidebar on mobile for better UX
        if (window.innerWidth < 1024) {
            setSidebarOpen(false);
        }
        // Focus on input after a brief delay
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    };

    const deleteSession = async (sessionId, e) => {
        e.stopPropagation();
        try {
            await axios.delete(`${API}/chat/session/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSessions(sessions.filter(s => s.session_id !== sessionId));
            if (currentSession?.session_id === sessionId) {
                startNewChat();
            }
            toast.success('Chat deleted');
        } catch (error) {
            toast.error('Failed to delete chat');
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMessage = {
            role: 'user',
            content: input.trim(),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post(`${API}/chat/send`, {
                message: userMessage.content,
                session_id: currentSession?.session_id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, assistantMessage]);
            
            if (!currentSession) {
                setCurrentSession({ session_id: response.data.session_id });
                fetchChatHistory();
            }
        } catch (error) {
            toast.error('Failed to send message', {
                description: error.response?.data?.detail || 'Please try again'
            });
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setLoading(false);
        }
    };

    const getSessionTitle = (session) => {
        const firstMessage = session.messages?.[0];
        if (firstMessage?.content) {
            return firstMessage.content.slice(0, 30) + (firstMessage.content.length > 30 ? '...' : '');
        }
        return 'New Chat';
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            
            <div className="flex pt-16 h-screen">
                {/* Sidebar */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <motion.aside
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 280, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-r border-border bg-card overflow-hidden"
                        >
                            <div className="p-4 h-full flex flex-col">
                                {/* Logo at top of sidebar */}
                                <Link to="/" className="flex items-center mb-4 px-2" data-testid="sidebar-logo">
                                    <AalimLogoCompact size={48} />
                                    <span className="font-heading text-2xl font-semibold text-foreground -ml-2">aalim<span className="text-secondary">.ai</span></span>
                                </Link>

                                <Button 
                                    onClick={startNewChat}
                                    className="w-full mb-4 bg-primary hover:bg-primary/90"
                                    data-testid="new-chat-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New Chat
                                </Button>

                                {!isPremium && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => navigate('/subscription')}
                                        className="w-full mb-4 border-secondary text-secondary hover:bg-secondary/10"
                                        data-testid="upgrade-btn"
                                    >
                                        <Crown className="w-4 h-4 mr-2" />
                                        Upgrade to Ustadh Mode
                                    </Button>
                                )}

                                {/* Sidebar Ad for Free Users */}
                                {showAds && (
                                    <div className="mb-4">
                                        <AdBanner placement="sidebar" />
                                    </div>
                                )}

                                <div className="text-xs font-medium text-muted-foreground mb-2 px-2">
                                    CHAT HISTORY
                                </div>

                                <ScrollArea className="flex-1">
                                    {loadingSessions ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                        </div>
                                    ) : sessions.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                            No chat history yet
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {sessions.map((session) => (
                                                <div
                                                    key={session.session_id}
                                                    onClick={() => loadSession(session.session_id)}
                                                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                                        currentSession?.session_id === session.session_id
                                                            ? 'bg-primary/10 text-primary'
                                                            : 'hover:bg-muted text-foreground'
                                                    }`}
                                                    data-testid={`chat-session-${session.session_id}`}
                                                >
                                                    <MessageSquare className="w-4 h-4 shrink-0" />
                                                    <span className="flex-1 truncate text-sm">
                                                        {getSessionTitle(session)}
                                                    </span>
                                                    <button
                                                        onClick={(e) => deleteSession(session.session_id, e)}
                                                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-destructive transition-opacity"
                                                        data-testid={`delete-session-${session.session_id}`}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>

                                {/* Navigation Links at bottom */}
                                <div className="mt-auto pt-4 border-t border-border space-y-1">
                                    <Link 
                                        to="/chat"
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm"
                                    >
                                        <MessageSquare className="w-4 h-4" />
                                        Chat
                                    </Link>
                                    <Link 
                                        to="/subscription"
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground text-sm transition-colors"
                                    >
                                        <Crown className="w-4 h-4" />
                                        Subscription
                                    </Link>
                                </div>
                            </div>
                        </motion.aside>
                    )}
                </AnimatePresence>

                {/* Toggle Sidebar Button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-card border border-border rounded-r-lg hover:bg-muted transition-colors"
                    style={{ left: sidebarOpen ? '280px' : '0' }}
                    data-testid="toggle-sidebar"
                >
                    {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>

                {/* Main Chat Area */}
                <main className="flex-1 flex flex-col">
                    {/* Disclaimer Banner */}
                    <div className="bg-secondary/10 border-b border-secondary/20 px-4 py-2">
                        <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm text-secondary">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <p>
                                <strong>Please note:</strong> Aalim.AI cannot issue fatwas or replace qualified scholars (ulema). 
                                For specific rulings, please consult your local imam or mufti.
                            </p>
                        </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                        {messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center px-4">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                                    <BookOpen className="w-8 h-8 text-primary" />
                                </div>
                                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                    Assalamu Alaikum
                                </h2>
                                <p className="text-muted-foreground max-w-md mb-6">
                                    Ask any question about Islam, Quran, Hadith, or Islamic jurisprudence.
                                    I'll provide answers from authentic sources.
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                                    {[
                                        'What does the Quran say about patience?',
                                        'How to perform Wudu correctly?',
                                        'What are the pillars of Islam?',
                                        'Tell me about the life of Prophet Muhammad ﷺ'
                                    ].map((suggestion, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setInput(suggestion)}
                                            className="px-3 py-1.5 text-sm rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                                            data-testid={`suggestion-${i}`}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="max-w-3xl mx-auto space-y-4">
                                {messages.map((msg, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                                                msg.role === 'user'
                                                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                                                    : 'bg-muted rounded-bl-sm'
                                            }`}
                                        >
                                            {msg.role === 'assistant' ? (
                                                <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                                                            strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                                                            em: ({children}) => <em className="italic">{children}</em>,
                                                            ul: ({children}) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                                                            ol: ({children}) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                                                            li: ({children}) => <li className="mb-1">{children}</li>,
                                                            h1: ({children}) => <h3 className="font-semibold text-base mb-2">{children}</h3>,
                                                            h2: ({children}) => <h4 className="font-semibold text-base mb-2">{children}</h4>,
                                                            h3: ({children}) => <h5 className="font-semibold text-sm mb-1">{children}</h5>,
                                                            code: ({children}) => <code className="bg-background/50 px-1 py-0.5 rounded text-xs">{children}</code>,
                                                            blockquote: ({children}) => (
                                                                <blockquote className="border-l-2 border-primary pl-3 italic my-2">
                                                                    {children}
                                                                </blockquote>
                                                            ),
                                                        }}
                                                    >
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                                {loading && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex justify-start"
                                    >
                                        <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
                                            <div className="flex gap-1">
                                                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </ScrollArea>

                    {/* Bottom Banner Ad for Free Users - positioned above input */}
                    {showAds && showBottomAd && (
                        <div className="border-t border-border bg-background">
                            <AdBanner 
                                placement="banner" 
                                onDismiss={() => setShowBottomAd(false)} 
                            />
                        </div>
                    )}
                    
                    {/* Input Area - Always visible at bottom */}
                    <div className="sticky bottom-0 bg-background border-t border-border p-4 z-10">
                        <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-2">
                            <Input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about Islam, Quran, or Hadith..."
                                className="flex-1"
                                disabled={loading}
                                data-testid="chat-input"
                            />
                            <Button 
                                type="submit" 
                                disabled={!input.trim() || loading}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="chat-send"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </Button>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
}
